/**
 * Casper Network
 */
import {
  Args,
  AuctionManagerEntryPoint,
  CLTypeKey,
  CLTypeList,
  CLTypeUInt8,
  CLValue,
  CLValueList,
  HttpHandler,
  Key as CLKey,
  PublicKey,
  RpcClient,
  Transaction,
  CasperNetwork,
  NativeTransferBuilder,
  ContractCallBuilder,
} from 'casper-js-sdk';
import { ActionsThresholds, AssociatedKey } from '@block-explorer/entities';
import { useGetCasperNetwork } from './casper-network-context';
import Big from 'big.js';

const GRPC_URL = config.cspr_rpc_url;

const handler = new HttpHandler(GRPC_URL);
export const casperService = new RpcClient(handler);

export enum TransferEntryPoint {
  token_transfer = 'token transfer',
}

export enum AssociatedKeysEntryPoint {
  contract_execution = 'contract execution',
}

const ASSOCIATED_KEYS_ENTRYPOINT = 'set_associated_keys';

/**
 * Calculation of the cost of a transaction by gas price.
 * config.transfer_cost
 */
export const calcTransactionCostMotes = (cost): number => {
  return Big(cost).mul(config.gas_price).toNumber();
};

export const getAuctionManagerDeployCost = (
  entryPoint: AuctionManagerEntryPoint
) => {
  switch (entryPoint) {
    case AuctionManagerEntryPoint.delegate:
      return config.delegate_cost;
    case AuctionManagerEntryPoint.undelegate:
      return config.undelegate_cost;
    case AuctionManagerEntryPoint.redelegate:
      return config.redelegate_cost;

    default:
      throw Error('getAuctionManagerDeployCost: unknown entry point');
  }
};

type GetCasperNetworkService = {
  casperNetworkService: CasperNetwork;
  makeChangeAssociatedKeysTransaction: (
    shouldUseDeploys: boolean,
    publicKey: string,
    contractHash: string,
    actionThresholds: ActionsThresholds,
    associatedKeys: AssociatedKey[],
    cost: string,
    timestamp: number,
    isMultisig?: boolean
  ) => Transaction;
  makeNativeAuctionTransaction: (
    shouldUseDeploys: boolean,
    contractEntryPoint: AuctionManagerEntryPoint,
    callerPublicKeyHex: string,
    validatorPublicKeyHex: string,
    redelegateValidatorPublicKeyHex: string | null,
    amountMotes: string,
    isMultisig?: boolean
  ) => Transaction;
  makeNativeTransferTransaction: (
    shouldUseDeploys: boolean,
    senderPublicKeyHex: string,
    recipientPublicKeyHex: string,
    amountMotes: string,
    transferIdMemo: string,
    callerPublicKeyHex: string | null,
    isMultisig?: boolean
  ) => Transaction;
};

export const useGetCasperNetworkService = (): GetCasperNetworkService => {
  const { networkMajorVersion } = useGetCasperNetwork();
  const casperNetworkService = new CasperNetwork(
    casperService,
    Number(networkMajorVersion)
  );

  const makeNativeAuctionTransaction = (
    shouldUseDeploys: boolean,
    contractEntryPoint: AuctionManagerEntryPoint,
    callerPublicKeyHex: string,
    validatorPublicKeyHex: string,
    redelegateValidatorPublicKeyHex: string | null,
    amountMotes: string,
    isMultisig: boolean = false
  ) => {
    const ttl = isMultisig
      ? Number(config.multisig_deploy_ttl)
      : config.deploy_ttl
        ? Number(config.deploy_ttl)
        : 0;

    const validatorKey = PublicKey.fromHex(validatorPublicKeyHex);
    const callerPublicKey = PublicKey.fromHex(callerPublicKeyHex);
    const newValidatorKey = redelegateValidatorPublicKeyHex
      ? PublicKey.fromHex(redelegateValidatorPublicKeyHex)
      : null;

    const paymentAmount: number =
      getAuctionManagerDeployCost(contractEntryPoint);

    switch (contractEntryPoint) {
      case AuctionManagerEntryPoint.delegate:
        //TODO: Remove this part after Ledger, Metamask and CasperDash wallets supports transactions
        if (shouldUseDeploys) {
          return new ContractCallBuilder()
            .from(callerPublicKey)
            .byHash(config.auction_manager_contract_hash)
            .entryPoint(AuctionManagerEntryPoint.delegate)
            .payment(
              calcTransactionCostMotes(paymentAmount),
              Number(config.gas_price)
            )
            .chainName(config.network_name)
            .runtimeArgs(
              Args.fromMap({
                validator: CLValue.newCLPublicKey(validatorKey),
                delegator: CLValue.newCLPublicKey(callerPublicKey),
                amount: CLValue.newCLUInt512(amountMotes),
              })
            )
            .ttl(ttl)
            .buildFor1_5();
        }
        return casperNetworkService.createDelegateTransaction(
          callerPublicKey,
          validatorKey,
          config.network_name,
          amountMotes,
          calcTransactionCostMotes(paymentAmount),
          ttl,
          config.auction_manager_contract_hash
        );

      case AuctionManagerEntryPoint.undelegate:
        //TODO: Remove this part after Ledger, Metamask and CasperDash wallets supports transactions
        if (shouldUseDeploys) {
          return new ContractCallBuilder()
            .from(callerPublicKey)
            .byHash(config.auction_manager_contract_hash)
            .entryPoint(AuctionManagerEntryPoint.undelegate)
            .chainName(config.network_name)
            .payment(
              calcTransactionCostMotes(paymentAmount),
              Number(config.gas_price)
            )
            .ttl(ttl)
            .runtimeArgs(
              Args.fromMap({
                validator: CLValue.newCLPublicKey(validatorKey),
                delegator: CLValue.newCLPublicKey(callerPublicKey),
                amount: CLValue.newCLUInt512(amountMotes),
              })
            )
            .buildFor1_5();
        }

        return casperNetworkService.createUndelegateTransaction(
          callerPublicKey,
          validatorKey,
          config.network_name,
          amountMotes,
          calcTransactionCostMotes(paymentAmount),
          ttl,
          config.auction_manager_contract_hash
        );

      case AuctionManagerEntryPoint.redelegate:
        if (newValidatorKey) {
          //TODO: Remove this part after Ledger, Metamask and CasperDash wallets supports transactions
          if (shouldUseDeploys) {
            return new ContractCallBuilder()
              .from(callerPublicKey)
              .byHash(config.auction_manager_contract_hash)
              .entryPoint('redelegate')
              .chainName(config.network_name)
              .payment(
                calcTransactionCostMotes(paymentAmount),
                Number(config.gas_price)
              )
              .runtimeArgs(
                Args.fromMap({
                  validator: CLValue.newCLPublicKey(validatorKey),
                  delegator: CLValue.newCLPublicKey(callerPublicKey),
                  amount: CLValue.newCLUInt512(amountMotes),
                  ...(newValidatorKey
                    ? {
                        new_validator: CLValue.newCLPublicKey(newValidatorKey),
                      }
                    : {}),
                })
              )
              .ttl(ttl)
              .buildFor1_5();
          }

          return casperNetworkService.createRedelegateTransaction(
            callerPublicKey,
            validatorKey,
            newValidatorKey,
            config.network_name,
            amountMotes,
            calcTransactionCostMotes(paymentAmount),
            ttl,
            config.auction_manager_contract_hash
          );
        } else {
          throw Error(
            'makeNativeAuctionTransaction: unknown new validator key'
          );
        }

      default:
        throw Error('makeNativeAuctionTransaction: unknown entry point');
    }
  };

  const makeNativeTransferTransaction = (
    shouldUseDeploys: boolean,
    senderPublicKeyHex: string,
    recipientPublicKeyHex: string,
    amountMotes: string,
    transferIdMemo: string,
    callerPublicKeyHex: string | null,
    isMultisig: boolean = false
  ) => {
    const ttl = isMultisig
      ? Number(config.multisig_deploy_ttl)
      : config.deploy_ttl
        ? Number(config.deploy_ttl)
        : 0;

    const callerPublicKey = callerPublicKeyHex ? callerPublicKeyHex : null;
    const recipientKey = PublicKey.fromHex(recipientPublicKeyHex);
    const senderKey = PublicKey.fromHex(
      callerPublicKey !== null ? callerPublicKey : senderPublicKeyHex
    );

    //TODO: Remove this part after Ledger, Metamask and CasperDash wallets supports transactions
    if (shouldUseDeploys) {
      const transferBuilder = new NativeTransferBuilder()
        .from(senderKey)
        .target(recipientKey)
        .amount(amountMotes)
        .chainName(config.network_name)
        .payment(
          calcTransactionCostMotes(config.transfer_cost),
          Number(config.gas_price)
        )
        .ttl(ttl);

      return transferBuilder.buildFor1_5();
    }

    return casperNetworkService.createTransferTransaction(
      senderKey,
      recipientKey,
      config.network_name,
      amountMotes,
      calcTransactionCostMotes(config.transfer_cost),
      ttl,
      Number(transferIdMemo),
      Number(config.gas_price)
    );
  };

  const makeChangeAssociatedKeysTransaction = (
    shouldUseDeploys: boolean,
    publicKey: string,
    contractPackageHash: string,
    actionThresholds: ActionsThresholds,
    associatedKeys: AssociatedKey[],
    cost: string,
    timestamp: number,
    isMultisig: boolean = false
  ) => {
    const senderPublicKey = PublicKey.fromHex(publicKey);

    const accountHashClListType = new CLTypeList(CLTypeKey);
    const accountHashClListValue = new CLValueList(
      accountHashClListType,
      associatedKeys.map((key) =>
        CLValue.newCLKey(CLKey.createByType(key.account_hash, 0))
      )
    );
    const accountHashCLValue = CLValue.newCLList(CLTypeKey);
    accountHashCLValue.list = accountHashClListValue;

    const weightClListType = new CLTypeList(CLTypeUInt8);
    const weightClListValue = new CLValueList(
      weightClListType,
      associatedKeys.map((key) => CLValue.newCLUint8(key.weight))
    );
    const weightCLValue = CLValue.newCLList(CLTypeUInt8);
    weightCLValue.list = weightClListValue;

    const args = {
      deployment_threshold: CLValue.newCLUint8(actionThresholds.deployment),
      key_management_threshold: CLValue.newCLUint8(
        actionThresholds.key_management
      ),
      weights: weightCLValue,
      keys: accountHashCLValue,
    };

    const ttl = isMultisig
      ? Number(config.multisig_deploy_ttl)
      : config.deploy_ttl
        ? Number(config.deploy_ttl)
        : undefined;

    //TODO: Remove this part after Ledger, Metamask and CasperDash wallets supports transactions
    if (shouldUseDeploys) {
      const contractCall = new ContractCallBuilder()
        .byPackageHash(contractPackageHash)
        .from(senderPublicKey)
        .entryPoint(ASSOCIATED_KEYS_ENTRYPOINT)
        .chainName(config.network_name)
        .runtimeArgs(Args.fromMap(args))
        .ttl(ttl)
        .payment(
          calcTransactionCostMotes(config.manage_associated_keys_cost),
          Number(config.gas_price)
        );
      return contractCall.buildFor1_5();
    }

    return casperNetworkService.createContractPackageCallTransaction(
      senderPublicKey,
      contractPackageHash,
      ASSOCIATED_KEYS_ENTRYPOINT,
      config.network_name,
      calcTransactionCostMotes(config.manage_associated_keys_cost),
      Args.fromMap(args),
      ttl
    );
  };

  return {
    casperNetworkService: casperNetworkService,
    makeNativeAuctionTransaction: makeNativeAuctionTransaction,
    makeChangeAssociatedKeysTransaction: makeChangeAssociatedKeysTransaction,
    makeNativeTransferTransaction: makeNativeTransferTransaction,
  };
};
